/**
 * The web twin of native `ScreenHeaderBucket` (dark tone).
 *
 * Same anatomy: a 40px icon tile or back button in the left slot, then a
 * left-aligned title with an optional subtitle, on the `primaryDark` canvas.
 * Only the dark tone is ported — the light tone exists natively for settings
 * flows, which this app does not have.
 *
 * The right slot carries native's pair, in native's order: Activity (`pulse`,
 * with an unread bubble) then Settings (the account avatar, or
 * `person-circle-outline` before one exists). Both are plain links, so
 * `interceptLinkClicks` routes them. The phone's hamburger is gone — sections
 * live in `MobileTabBar` now.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { arrowBackOutline, personCircleOutline, pulse } from 'ionicons/icons';
import IonIcon from '../../components/IonIcon';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { ACTIVITY_PATH, SETTINGS_PATH } from '../workTabs';
import type { AssigneeIdentity } from '../useAssigneeIdentity';
import { cn } from '../../lib/utils';
import EditableCell from './EditableCell';

interface Props {
  title: string;
  subtitle?: string | null;
  /** Data-URL from `ionicons/icons`, rendered in the left tile. */
  icon?: string;
  /** Round cover photo — native `iconImageUrl` on ScreenHeaderBucket. */
  imageUrl?: string | null;
  /**
   * When set, the leading image (or icon tile) is a button. Bucket and
   * routine studios use this for the cover crop.
   */
  onLeadingPress?: () => void;
  leadingLabel?: string;
  /**
   * When set, the title is the same inline field the desktop studio header
   * uses, instead of a static heading.
   */
  titleField?: {
    ariaLabel: string;
    placeholder?: string;
    onCommit: (next: string) => void;
  };
  /** Solid ionicons need paint rather than stroke. */
  iconFilled?: boolean;
  /** When set, the left slot becomes a back button instead of an icon tile. */
  backHref?: string;
  /** Page-specific trailing controls, placed before the Activity/Settings pair. */
  actions?: ReactNode;
  /**
   * Renders the Activity + Settings pair. Off by default so drill-down screens
   * keep the plain back-and-title header native gives them.
   */
  showAccountActions?: boolean;
  /** Drives the Settings avatar. Null when signed out. */
  identity?: AssigneeIdentity | null;
  /** Unread activity events. Native hides the bubble at zero. */
  activityCount?: number;
  className?: string;
}

function initials(name: string | null | undefined): string {
  if (!name) return 'X9';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || 'X9';
}

export default function ScreenHeader({
  title,
  subtitle,
  icon,
  imageUrl,
  onLeadingPress,
  leadingLabel,
  titleField,
  iconFilled = false,
  backHref,
  actions,
  showAccountActions = false,
  identity,
  activityCount = 0,
  className,
}: Props) {
  const { t } = useTranslation('web');

  return (
    <header
      className={cn(
        'bg-x9-marine px-4 pb-5 pt-[max(1rem,env(safe-area-inset-top))]',
        className,
      )}
    >
      <div className="flex w-full items-center gap-3">
        {backHref ? (
          <a
            href={backHref}
            aria-label={t('nav.back')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white transition hover:bg-white/20"
          >
            <IonIcon icon={arrowBackOutline} size={20} />
          </a>
        ) : imageUrl ? (
          <LeadingFace
            label={leadingLabel}
            onPress={onLeadingPress}
            className="size-10 shrink-0 overflow-hidden rounded-full"
          >
            <img
              src={imageUrl}
              alt=""
              className="size-10 shrink-0 rounded-full object-cover"
            />
          </LeadingFace>
        ) : icon ? (
          <LeadingFace
            label={leadingLabel}
            onPress={onLeadingPress}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white"
          >
            <IonIcon icon={icon} size={20} filled={iconFilled} />
          </LeadingFace>
        ) : null}

        <div className="min-w-0 flex-1">
          {titleField ? (
            <EditableCell
              value={title}
              ariaLabel={titleField.ariaLabel}
              placeholder={titleField.placeholder}
              onCommit={titleField.onCommit}
              className="min-w-0 !border-transparent !bg-transparent !px-0 !py-0 !text-lg !font-bold !leading-tight !text-white !tracking-tight placeholder:!text-white/50 hover:!border-white/30 focus:!border-white/60 focus:!bg-white/10"
            />
          ) : (
            <h1 className="truncate text-lg font-bold leading-tight text-white">{title}</h1>
          )}
          {subtitle ? (
            <p className="mt-0.5 truncate text-xs text-white/60">{subtitle}</p>
          ) : null}
        </div>

        {actions || showAccountActions ? (
          <div className="flex shrink-0 items-center gap-2">
            {actions}
            {showAccountActions ? (
              <>
                <a
                  href={ACTIVITY_PATH}
                  aria-label={
                    activityCount > 0
                      ? `${t('header.activity')} (${activityCount})`
                      : t('header.activity')
                  }
                  className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-x9-marine text-white shadow-[0_6px_16px_rgba(0,0,0,0.35)] ring-1 ring-white/15 transition hover:bg-white/10"
                >
                  <IonIcon icon={pulse} size={24} />
                  {activityCount > 0 ? (
                    <span
                      aria-hidden="true"
                      className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-x9-cyanLight px-1 text-center text-[10px] font-bold leading-[18px] text-x9-marine"
                    >
                      {activityCount > 99 ? '99+' : activityCount}
                    </span>
                  ) : null}
                </a>

                <a
                  href={SETTINGS_PATH}
                  aria-label={t('header.settings')}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white transition hover:bg-white/10"
                >
                  {identity?.photoURL ? (
                    <Avatar className="size-10">
                      <AvatarImage
                        src={identity.photoURL}
                        alt=""
                        referrerPolicy="no-referrer"
                      />
                      <AvatarFallback className="bg-white/15 text-xs font-semibold text-white">
                        {initials(identity.displayName)}
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <IonIcon icon={personCircleOutline} size={24} />
                  )}
                </a>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </header>
  );
}

function LeadingFace({
  label,
  onPress,
  className,
  children,
}: {
  label?: string;
  onPress?: () => void;
  className?: string;
  children: ReactNode;
}) {
  if (!onPress) return <span className={className}>{children}</span>;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onPress}
      className={cn('cursor-pointer bg-transparent p-0 text-inherit', className)}
    >
      {children}
    </button>
  );
}
